import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

export function useCalculatorValidation({
  minimumValue,
  maximumValue,
  unit,
  assetName
}: {
  minimumValue?: number
  maximumValue: number
  unit: string
  assetName?: string
}) {
  const { t } = useTranslation()
  const max = maximumValue ?? 1000000
  const min = minimumValue ?? 0

  const useInputOutsideRangeValidation = useMemo(() => {
    return z.number().refine(value => value >= min && value <= max, {
      message: t('common:validations.inputIsOutOfRange', {
        minimumValue: min,
        maximumValue: max,
        unit: unit
      })
    })
  }, [min, max, unit, t])

  const useInputAboveMaximumValueValidation = useMemo(() => {
    return z.number().refine(value => value <= max, {
      message: t('common:validations.aboveMaximumValue', {
        maximumValue: max,
        unit: unit
      })
    })
  }, [max, unit, t])

  const useInputIsOutOfRangeForSpecificAsset = useMemo(() => {
    return z.number().refine(value => value >= min && value <= max, {
      message: t('common:validations.inputIsOutOfRangeForSpecificAsset', {
        minimumValue: min,
        maximumValue: max,
        unit: unit,
        assetName: assetName
      })
    })
  }, [max, min, unit, t, assetName])

  return {
    useInputOutsideRangeValidation,
    useInputAboveMaximumValueValidation,
    useInputIsOutOfRangeForSpecificAsset
  }
}
